import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { StoneDevice } from '@/core/domain/stone/devices/devices.types';
import {
  StoneOrderHasOpenOrderResponse,
  StoneOrderOriginType,
  StoneOrderStatusType,
} from '@/core/domain/stone/orders/orders.types';
import useHasStoneDeviceProcess, {
  UseHasStoneDeviceProcessProps,
} from '@/hooks/useHasStoneDeviceProcess';
import useStoneHasOrderInfo from '@/hooks/useStoneHasOrderInfo';
import { Button } from 'antd';
import { useCallback, useEffect, useState } from 'react';

interface Props {
  watch: UseHasStoneDeviceProcessProps;
  originCode?: string;
  onFinish: (type: 'order' | 'cancelation') => void;
}

export default function StoneOrderProcessInfo({
  watch,
  onFinish,
  originCode,
}: Props) {
  const [orderInfo, setOrderInfo] = useState<StoneOrderHasOpenOrderResponse>();
  const [device, setDevice] = useState<StoneDevice>();

  const { cancelOrder, isLoading, resultData } = useStoneHasOrderInfo({
    deviceId: device?.id,
    isStoneEnabled: !!orderInfo && !!device,
    onFinish,
  });

  const hasDeviceProcess = useHasStoneDeviceProcess(watch);

  useEffect(() => {
    hasDeviceProcess((data) => {
      setOrderInfo(data?.orderInfo);
      setDevice(data?.device);
    });
  }, [hasDeviceProcess]);

  const isProcessing =
    resultData?.data?.orderStatus ===
      StoneOrderStatusType.processing_cancellation ||
    resultData?.data?.orderStatus === StoneOrderStatusType.processing_order;

  const isProcessingOrder =
    resultData?.data?.orderStatus === StoneOrderStatusType.processing_order;

  const isProcessingCancellation =
    resultData?.data?.orderStatus ===
    StoneOrderStatusType.processing_cancellation;

  const type =
    resultData?.data?.originType === StoneOrderOriginType.SALE
      ? 'Venda direta'
      : 'Orçamento';

  const getMessage = useCallback(() => {
    switch (resultData?.data?.orderStatus) {
      case StoneOrderStatusType.processing_cancellation:
        return `Processando cancelamento do pedido ${
          type === 'Orçamento' ? 'do' : 'da'
        } "${type}" referente ao código "${originCode}"...`;

      case StoneOrderStatusType.processing_order:
        return `Processando pagamento do pedido ${
          type === 'Orçamento' ? 'do' : 'da'
        } "${type}" referente ao código "${originCode}", se necessário cancele o pedido de pagamento Stone...`;

      default:
        return 'Carregando informações do processamento com a Stone...';
    }
  }, [resultData?.data?.orderStatus, originCode, type]);

  if (!isLoading && !resultData?.data?.orderId) return null;

  return (
    <ModalLoadingOverlay
      disableLoading={!isProcessing}
      loaderSize={'large'}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backdropFilter: 'blur(1px)',
        zIndex: 10,
      }}
      key={resultData?.data?.orderStatus}
      messages={
        isLoading
          ? ['Carregando informações da Stone']
          : [
              <>
                <div style={{ fontWeight: 'bold' }}>
                  {isProcessingOrder && 'Pedido em processamento com a Stone'}
                  {isProcessingCancellation &&
                    'Cancelamento em processamento com a Stone'}
                </div>

                <small>{getMessage()}</small>

                {isProcessingOrder && (
                  <div style={{ marginTop: 16 }}>
                    <Button type="primary" danger onClick={cancelOrder}>
                      Cancelar pedido "{type}/{originCode}"
                    </Button>
                  </div>
                )}
              </>,
            ]
      }
    />
  );
}
